import React, { useRef, useState } from "react";
import { FiPaperclip, FiX, FiEdit2, FiRotateCcw, FiTrash2, FiCheck } from "react-icons/fi";
import { FaEraser } from "react-icons/fa";
import MapaLayout from "./MapaLayout";
import {
  MEDIAL_SILHOUETTE,
  POSTERIOR_SILHOUETTE,
  POPLITEA_RIBBON,
  VSM_SPINE, VSM_HALF,
  VSP_SPINE, VSP_HALF,
  LANDMARK_MAGNA, LANDMARK_PARVA,
  FEMORAL_TRUNK_SPINE, FEMORAL_TRUNK_HALF, FEMORAL_COMUM_FIM, FEMORAL_COMUM_TOPO,
  FEMORAL_PROFUNDA_SPINE, FEMORAL_PROFUNDA_HALF,
  CORES,
  construirSegmentosVeia,
  corStatusProfundo,
  fitaVeiaSimples,
  posicaoPerfurante,
  trianguloPontos,
  PERFURANTE_TRIANGULO_RAIO,
} from "../utils/vascularMapping";
import { SafenaMagnaExtra, SafenaParvaExtra } from "./SafenaExtraFields";
import {
  profOptions, supOptions, perfurantesStatusOptions, perfurantesSegmentoOptions,
  normalizarVarizesLado,
  montarLaudo,
} from "../utils/mmiiVenosoLaudo";

// Layout simples e direto: só 2 vistas (medial + posterior — as mesmas do
// Esquema de Mapeamento), sem régua/grade. As veias profundas da coxa
// (Femoral Comum/Superficial/Profunda) entram na vista medial, ao lado da
// Safena Magna; as veias profundas da panturrilha entram na vista
// posterior — Tibiais/Gastrocnêmicas/Soleares como pequenos marcadores
// (ficam muito próximas entre si na realidade; tentar traçar 4 linhas
// separadas ali colide com a Safena Parva e entre si).
const COL_WIDTH = 320;
const COLS = [
  { key: "medial", label: "medial", tx: -68 },
  { key: "posterior", label: "posterior", tx: COL_WIDTH - 75 },
];
const VIEW_TY = 16;
export const VIEW_W = COL_WIDTH * 2;
export const VIEW_H = 660;

// dHalo: quando a área de clique é menor que a veia (safena parva, que não
// pode roubar o clique da poplítea), o sombreamento usa a veia inteira.
function hitPath(d, onClick, key, selecionado, largura = 10, dHalo) {
  const ativo = selecionado === key;
  return (
    <>
      {ativo && dHalo && (
        <path d={dHalo} fill="none" stroke="#0eb8d0" strokeOpacity={0.35} strokeWidth={largura} strokeLinecap="round" pointerEvents="none" />
      )}
      <path
        d={d}
        fill="none"
        stroke={ativo && !dHalo ? "#0eb8d0" : "#000"}
        strokeOpacity={ativo && !dHalo ? 0.35 : 0.001}
        strokeWidth={largura}
        strokeLinecap="round"
        style={{ cursor: "pointer" }}
        onClick={onClick}
      />
    </>
  );
}

// Marcadores compactos (bolinha + rótulo curto) para as veias profundas da
// panturrilha — mais fáceis de tocar num tablet do que 4 linhas finas e
// coladas, e continuam dentro do contorno da perna.
const CALF_DOTS = [
  { key: "Veias Gastrocnêmicas", label: "Gc", x: 177, y: 402 },
  { key: "Veias Tibiais anteriores", label: "Ta", x: 157, y: 402 },
  { key: "Veias Soleares", label: "So", x: 177, y: 428 },
  { key: "Veias Tibiais posteriores", label: "Tp", x: 157, y: 428 },
];

// Varizes são todas vermelhas: o tipo é dado só pelo calibre do traço
// (superficial grossa, reticular média, micro fina) — ver TRACO_LARGURA.
export const VARIZ_COR = "#d62828";
export const VARIZ_CORES = {
  "Varizes Superficiais": VARIZ_COR,
  "Varizes Reticulares": VARIZ_COR,
  "Microvarizes": VARIZ_COR,
  // tromboflebite superficial: traço preto, para destacar das varizes
  "Tromboflebite Superficial": "#111111",
};

// O contorno do ícone de variz é uma elipse (mais alta que larga, não um
// círculo): a faixa livre entre a perna e a Safena Magna na vista medial é
// estreita na horizontal mas sobra bastante espaço na vertical, então
// esticar pra cima/baixo é o jeito de deixar o alvo bem maior (o dobro de
// área do círculo antigo) sem esbarrar na Safena Magna nem sair do
// contorno da perna. Valores verificados (programaticamente, com folga)
// contra os dois limites em todos os 4 pontos — ver vascularMapping.test.js.
export const VARIZ_ICON_RX = 8;
export const VARIZ_ICON_RY_VAZIO = 12.25;
export const VARIZ_ICON_RY_TIPO = 20.25;

// Ícone por tipo de variz, "colado" na região clicada — sem tipo, mostra um
// círculo tracejado com "+" (toque pra marcar).
function VarizIcon({ tipo, cx, cy, ativo, escala = 1 }) {
  if (escala !== 1) {
    return (
      <g transform={`translate(${cx},${cy}) scale(${escala}) translate(${-cx},${-cy})`}>
        <VarizIcon tipo={tipo} cx={cx} cy={cy} ativo={ativo} />
      </g>
    );
  }
  const cor = VARIZ_CORES[tipo];
  if (!tipo) {
    return (
      <g>
        <ellipse cx={cx} cy={cy} rx={VARIZ_ICON_RX} ry={VARIZ_ICON_RY_VAZIO} fill="#fff" fillOpacity={0.6} stroke={ativo ? "#0eb8d0" : "#9aa7b0"} strokeWidth={ativo ? 2.2 : 1.4} strokeDasharray="3 2.5" />
        <line x1={cx - 4} y1={cy} x2={cx + 4} y2={cy} stroke={ativo ? "#0eb8d0" : "#9aa7b0"} strokeWidth={1.4} />
        <line x1={cx} y1={cy - 4} x2={cx} y2={cy + 4} stroke={ativo ? "#0eb8d0" : "#9aa7b0"} strokeWidth={1.4} />
      </g>
    );
  }
  if (tipo === "Varizes Superficiais") {
    // squiggle grosso (traço tortuoso, como uma variz visível)
    return (
      <g>
        <ellipse cx={cx} cy={cy} rx={VARIZ_ICON_RX} ry={VARIZ_ICON_RY_TIPO} fill="#fff" stroke={ativo ? "#0eb8d0" : cor} strokeWidth={ativo ? 2.4 : 1.4} />
        <path d={`M ${cx - 6.5},${cy + 3.9} Q ${cx - 3.25},${cy - 5.2} ${cx},${cy - 1.3} Q ${cx + 3.25},${cy + 3.9} ${cx + 6.5},${cy - 3.9}`} fill="none" stroke={cor} strokeWidth={2.4} strokeLinecap="round" />
      </g>
    );
  }
  if (tipo === "Varizes Reticulares") {
    // pequena malha/rede (linhas finas cruzadas)
    return (
      <g>
        <ellipse cx={cx} cy={cy} rx={VARIZ_ICON_RX} ry={VARIZ_ICON_RY_TIPO} fill="#fff" stroke={ativo ? "#0eb8d0" : cor} strokeWidth={ativo ? 2.4 : 1.4} />
        <path d={`M ${cx - 6.5},${cy - 3.9} L ${cx + 6.5},${cy - 3.9} M ${cx - 6.5},${cy + 3.9} L ${cx + 6.5},${cy + 3.9} M ${cx - 3.9},${cy - 6.5} L ${cx - 3.9},${cy + 6.5} M ${cx + 3.9},${cy - 6.5} L ${cx + 3.9},${cy + 6.5}`} stroke={cor} strokeWidth={1.2} />
      </g>
    );
  }
  // Microvarizes: pequeno buquê de tracinhos finos
  return (
    <g>
      <ellipse cx={cx} cy={cy} rx={VARIZ_ICON_RX} ry={VARIZ_ICON_RY_TIPO} fill="#fff" stroke={ativo ? "#0eb8d0" : cor} strokeWidth={ativo ? 2.4 : 1.4} />
      <path d={`M ${cx - 5.2},${cy - 2.6} l 3.25,1.95 M ${cx + 1.95},${cy - 5.2} l 1.3,3.9 M ${cx - 1.3},${cy + 1.3} l 3.9,2.6 M ${cx + 2.6},${cy + 2.6} l 2.6,-1.3`} stroke={cor} strokeWidth={1.5} strokeLinecap="round" />
    </g>
  );
}

// O mesmo ícone do desenho, em tamanho de legenda.
export function VarizLegendaIcone({ tipo }) {
  return (
    <svg width={10} height={22} viewBox="-9.5 -21.5 19 43" style={{ verticalAlign: "middle", flexShrink: 0 }} aria-hidden="true">
      <VarizIcon tipo={tipo} cx={0} cy={0} ativo={false} />
    </svg>
  );
}

// Pontos de variz: 8 na face anterior (vista "medial" do desenho, com a
// safena magna) e 8 na posterior. Ícone em 70% do tamanho para caber 8 por
// vista, cada um na faixa livre entre o contorno da perna e as veias
// (conferido em vascularMapping.test.js).
export const VARIZ_ESCALA = 0.7;
export const VARIZ_SPOTS = [
  { regiao: "antCoxaProximal", view: "medial", x: 157, y: 95 },
  { regiao: "antCoxaMedia", view: "medial", x: 160, y: 175 },
  { regiao: "antCoxaDistal", view: "medial", x: 162.5, y: 235 },
  { regiao: "antPernaProximal", view: "medial", x: 136, y: 375 },
  { regiao: "antPernaMedia", view: "medial", x: 134, y: 435 },
  { regiao: "antPernaDistal", view: "medial", x: 134, y: 495 },
  { regiao: "antTornozelo", view: "medial", x: 131, y: 538 },
  { regiao: "antPe", view: "medial", x: 148, y: 594 },
  { regiao: "postCoxaProximal", view: "posterior", x: 150, y: 95 },
  { regiao: "postCoxaMedia", view: "posterior", x: 150, y: 175 },
  { regiao: "postCoxaDistal", view: "posterior", x: 150, y: 255 },
  { regiao: "postPernaProximal", view: "posterior", x: 170, y: 368 },
  { regiao: "postPernaMedia", view: "posterior", x: 167, y: 467 },
  { regiao: "postPernaDistal", view: "posterior", x: 162, y: 507 },
  { regiao: "postTornozelo", view: "posterior", x: 153, y: 550 },
  { regiao: "postPe", view: "posterior", x: 150, y: 597 },
];

// Varizes desenhadas à mão (botão "Desenhar varizes"): todas vermelhas, o
// calibre do traço é o que diferencia o tipo.
export const TRACO_LARGURA = {
  "Varizes Superficiais": 5.4,
  "Varizes Reticulares": 2.6,
  "Microvarizes": 0.8,
  "Tromboflebite Superficial": 3.6,
};

// Amostra do traço (cor + calibre), para legendas e botões das canetas.
export function VarizLinhaLegenda({ tipo, largura = 26 }) {
  return (
    <svg width={largura} height={10} viewBox="0 0 26 10" style={{ verticalAlign: "middle", flexShrink: 0 }} aria-hidden="true">
      <path d="M 2.5,7 Q 8,1 13,5 T 23.5,3" fill="none" stroke={VARIZ_CORES[tipo]} strokeWidth={TRACO_LARGURA[tipo]} strokeLinecap="round" />
    </svg>
  );
}
const VISTA_DA_COLUNA = { medial: "anterior", posterior: "posterior" };

// Curva suave passando pelos pontos (quadráticas pelos pontos médios).
export function caminhoTraco(pts) {
  if (!pts || !pts.length) return "";
  const [x0, y0] = pts[0];
  if (pts.length === 1) return `M ${x0},${y0} l 0.5,0.5`;
  let d = `M ${x0},${y0}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const [x, y] = pts[i];
    const [nx, ny] = pts[i + 1];
    d += ` Q ${x},${y} ${(x + nx) / 2},${(y + ny) / 2}`;
  }
  const [xl, yl] = pts[pts.length - 1];
  return `${d} L ${xl},${yl}`;
}

const PROFUNDA_LABELS = {
  "Veia Femoral Comum": "Veia Femoral Comum",
  "Veia Femoral Superficial": "Veia Femoral Superficial",
  "Veia Femoral Profunda": "Veia Femoral Profunda",
  "Veia Poplítea": "Veia Poplítea",
  "Veias Tibiais anteriores": "Veias Tibiais Anteriores",
  "Veias Tibiais posteriores": "Veias Tibiais Posteriores",
  "Veias Gastrocnêmicas": "Veias Gastrocnêmicas",
  "Veias Soleares": "Veias Soleares",
};

// Desenho das duas vistas (anterior + posterior) de UM membro. O Mapa Interativo
// usa com onSelecionar (clicável); o Mapeamento Visual e o PDF usam sem ele
// (estático) — é o mesmo desenho, então o PDF impresso sai igual ao Mapa Interativo.
export function DesenhoMMIIVenoso({
  lado, profundas, superficiais, magna, parva, perfurantes, varizes,
  onSelecionar, chaveAtiva = null, width = "100%", height, style,
  // ferramenta: tipo de variz (caneta) ou "borracha"; null = modo normal
  ferramenta = null, onTracosChange,
}) {
  const l = lado;
  const p = profundas || {};
  const s = superficiais || {};
  const m = magna || {};
  const pv = parva || {};
  const perfs = Array.isArray(perfurantes) ? perfurantes : [];
  const vz = normalizarVarizesLado(varizes);
  const tracos = vz.tracos || [];
  const editandoTracos = typeof onTracosChange === "function" && !!ferramenta;
  const apagando = editandoTracos && ferramenta === "borracha";
  const riscando = editandoTracos && !apagando;
  const interativo = typeof onSelecionar === "function";
  // enquanto desenha ou apaga, tocar no desenho não abre a caixa do vaso
  const selecionar = (tipo, key) => { if (interativo && !editandoTracos) onSelecionar(tipo, key); };
  const clicavel = interativo && !editandoTracos ? { cursor: "pointer" } : undefined;

  // Traço em andamento: o ref guarda os pontos (sem depender do ciclo do
  // React entre um movimento e outro) e o estado redesenha a linha.
  const tracoRef = useRef(null);
  const [tracoAtual, setTracoAtual] = useState(null);

  // Ponto do toque/mouse no sistema do desenho da perna (o grupo espelhado
  // já desfaz o espelho da perna direita, então o traço é salvo sempre no
  // sistema da perna esquerda, como os VARIZ_SPOTS).
  function pontoNoDesenho(e) {
    const alvo = e.currentTarget;
    const svg = alvo.ownerSVGElement;
    const matriz = alvo.getScreenCTM && alvo.getScreenCTM();
    if (!svg || !matriz) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const r = pt.matrixTransform(matriz.inverse());
    return [Math.round(r.x * 10) / 10, Math.round(r.y * 10) / 10];
  }
  function terminarTraco() {
    const t = tracoRef.current;
    tracoRef.current = null;
    setTracoAtual(null);
    if (t) onTracosChange([...tracos, t]);
  }
  function camadaDesenho(vista) {
    if (!riscando) return null;
    return (
      <rect
        x={0} y={-VIEW_TY} width={300} height={VIEW_H} fill="#fff" fillOpacity={0.001}
        style={{ touchAction: "none", cursor: "crosshair" }}
        onPointerDown={(e) => {
          const pt = pontoNoDesenho(e);
          if (!pt) return;
          e.preventDefault();
          try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* navegador sem suporte */ }
          tracoRef.current = { tipo: ferramenta, vista, pts: [pt] };
          setTracoAtual(tracoRef.current);
        }}
        onPointerMove={(e) => {
          const t = tracoRef.current;
          if (!t || t.vista !== vista) return;
          const pt = pontoNoDesenho(e);
          if (!pt) return;
          const [ux, uy] = t.pts[t.pts.length - 1];
          if (Math.hypot(pt[0] - ux, pt[1] - uy) < 1.5) return;
          tracoRef.current = { ...t, pts: [...t.pts, pt] };
          setTracoAtual(tracoRef.current);
        }}
        onPointerUp={terminarTraco}
        onPointerCancel={terminarTraco}
      />
    );
  }
  function desenharTracos(vista) {
    return (
      <>
        {tracos.map((t, i) => (t.vista === vista ? (
          <g key={`traco-${i}`}>
            <path d={caminhoTraco(t.pts)} fill="none" stroke={VARIZ_CORES[t.tipo]} strokeWidth={TRACO_LARGURA[t.tipo]} strokeLinecap="round" strokeLinejoin="round" pointerEvents="none" />
            {apagando && (
              <path
                d={caminhoTraco(t.pts)} fill="none" stroke="#000" strokeOpacity={0.001} strokeWidth={14} strokeLinecap="round"
                style={{ cursor: "pointer" }}
                onClick={() => onTracosChange(tracos.filter((_, j) => j !== i))}
              />
            )}
          </g>
        ) : null))}
        {tracoAtual && tracoAtual.vista === vista && (
          <path d={caminhoTraco(tracoAtual.pts)} fill="none" stroke={VARIZ_CORES[tracoAtual.tipo]} strokeWidth={TRACO_LARGURA[tracoAtual.tipo]} strokeLinecap="round" strokeLinejoin="round" pointerEvents="none" />
        )}
      </>
    );
  }

  // A geometria (silhuetas, veias) foi desenhada para a perna ESQUERDA.
  // Para a perna DIREITA, espelha o desenho (a perna direita é a imagem
  // espelhada da esquerda). Os rótulos de texto ficam fora do grupo
  // espelhado (usando mx() para a posição) para não saírem de cabeça
  // para baixo / invertidos.
  const mirrored = l === "Direito";
  const mirrorTransform = mirrored ? "translate(300,0) scale(-1,1)" : undefined;
  const mx = (x) => (mirrored ? 300 - x : x);

  // ---- Vista MEDIAL: JSF + Safena Magna + Femoral Comum/Superficial/Profunda ----
  const magnaResult = construirSegmentosVeia({
    spine: VSM_SPINE, half: VSM_HALF, landmark: LANDMARK_MAGNA,
    status: s["Safena Magna"], ini: m.inicio, fim: m.fim, iniVal: m.inicio_valor, fimVal: m.fim_valor,
  });
  const magnaHitD = fitaVeiaSimples(VSM_SPINE, VSM_HALF, LANDMARK_MAGNA.top, LANDMARK_MAGNA.tornozelo);
  const jsfCor = CORES[s["JSF"]] || CORES["pérvia e competente"];

  const comumD = fitaVeiaSimples(FEMORAL_TRUNK_SPINE, FEMORAL_TRUNK_HALF, FEMORAL_COMUM_TOPO, FEMORAL_COMUM_FIM);
  const superficialD = fitaVeiaSimples(FEMORAL_TRUNK_SPINE, FEMORAL_TRUNK_HALF, FEMORAL_COMUM_FIM, LANDMARK_MAGNA.joelho);
  const profundaFemD = fitaVeiaSimples(FEMORAL_PROFUNDA_SPINE, FEMORAL_PROFUNDA_HALF, FEMORAL_PROFUNDA_SPINE[0][1], FEMORAL_PROFUNDA_SPINE[FEMORAL_PROFUNDA_SPINE.length - 1][1]);

  // ---- Vista POSTERIOR: JSP + Veia Poplítea + Safena Parva + panturrilha ----
  const parvaResult = construirSegmentosVeia({
    spine: VSP_SPINE, half: VSP_HALF, landmark: LANDMARK_PARVA,
    status: s["Safena Parva"], ini: pv.inicio, fim: pv.fim, iniVal: pv.inicio_valor, fimVal: pv.fim_valor,
  });
  // Área de clique começa abaixo da faixa da Veia Poplítea (POPLITEA_RIBBON vai
  // até y~388), para não roubar o clique destinado a ela logo abaixo do JSP.
  const parvaHitD = fitaVeiaSimples(VSP_SPINE, VSP_HALF, 382, LANDMARK_PARVA.tornozelo);
  // Sombreamento da seleção: a parva inteira, desde a JSP.
  const parvaHaloD = fitaVeiaSimples(VSP_SPINE, VSP_HALF, LANDMARK_PARVA.top, LANDMARK_PARVA.tornozelo);
  const jspCor = CORES[s["JSP"]] || CORES["pérvia e competente"];

  // ---- Marcadores de perfurante insuficiente (vista medial) ----
  // Quando há mais de um perfurante na mesma altura, empilha os marcadores
  // na VERTICAL (não na horizontal): perto do joelho a perna afunila e um
  // deslocamento lateral maior jogava os marcadores extras pra fora do
  // desenho.
  const perfMarkers = perfs
    .filter((perf) => perf && perf.status === "pérvia e incompetente" && perf.segmento)
    .map((perf, idx) => {
      const pos = posicaoPerfurante(perf.segmento, perf.valor);
      if (!pos) return null;
      return { x: pos.x, y: pos.y + idx * 12 };
    })
    .filter(Boolean);

  const colMedial = COLS[0], colPosterior = COLS[1];

  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} width={width} height={height} style={riscando ? { ...style, touchAction: "none", userSelect: "none" } : style}>
      {/* MEDIAL: JSF + Safena Magna + Femoral Comum/Superficial/Profunda + perfurantes */}
      <g transform={`translate(${colMedial.tx},${VIEW_TY})`}>
        <g transform={mirrorTransform}>
          <path d={MEDIAL_SILHOUETTE} fill="#f3d9bb" stroke="#a97a4e" strokeWidth={1.5} />
          <path d={profundaFemD} fill={corStatusProfundo(p["Veia Femoral Profunda"])} pointerEvents="none" />
          {interativo && hitPath(profundaFemD, () => selecionar("profunda", "Veia Femoral Profunda"), "profunda:Veia Femoral Profunda", chaveAtiva, 8)}
          <path d={comumD} fill={corStatusProfundo(p["Veia Femoral Comum"])} pointerEvents="none" />
          {interativo && hitPath(comumD, () => selecionar("profunda", "Veia Femoral Comum"), "profunda:Veia Femoral Comum", chaveAtiva, 8)}
          <path d={superficialD} fill={corStatusProfundo(p["Veia Femoral Superficial"])} pointerEvents="none" />
          {interativo && hitPath(superficialD, () => selecionar("profunda", "Veia Femoral Superficial"), "profunda:Veia Femoral Superficial", chaveAtiva, 8)}
          {magnaResult.segments.map((seg, i) => (
            <path key={i} d={seg.d} fill={seg.tracejado ? "none" : seg.color} stroke={seg.tracejado ? seg.color : "none"} strokeDasharray={seg.tracejado ? "5 5" : undefined} strokeWidth={seg.tracejado ? 2 : undefined} pointerEvents="none" />
          ))}
          {interativo && hitPath(magnaHitD, () => selecionar("superficial", "Safena Magna"), "superficial:Safena Magna", chaveAtiva)}
          <circle cx={VSM_SPINE[0][0]} cy={VSM_SPINE[0][1]} r={8} fill={jsfCor} stroke="#fff" strokeWidth={1.5} style={clicavel} onClick={() => selecionar("superficial", "JSF")} />
          {perfMarkers.map((mk, i) => (
            <polygon key={i} points={trianguloPontos(mk.x, mk.y, PERFURANTE_TRIANGULO_RAIO)} fill={CORES["pérvia e incompetente"]} stroke="#fff" strokeWidth={1.3} strokeLinejoin="round" />
          ))}
          {/* varizes marcadas por região (exames antigos / formulário): só exibe */}
          {VARIZ_SPOTS.filter((spot) => spot.view === "medial" && vz[spot.regiao]).map((spot) => (
            <g key={spot.regiao} pointerEvents="none">
              <VarizIcon tipo={vz[spot.regiao]} cx={spot.x} cy={spot.y} ativo={false} escala={VARIZ_ESCALA} />
            </g>
          ))}
          {desenharTracos(VISTA_DA_COLUNA.medial)}
          {camadaDesenho(VISTA_DA_COLUNA.medial)}
        </g>
        <text x={mx(212.7)} y={40} fontFamily="monospace" fontSize="9.5" fill="#1a2530" textAnchor={mirrored ? "start" : "end"}>JSF</text>
        <text x={mx(111)} y={95} fontFamily="monospace" fontSize="9.5" fill="#1a2530" textAnchor={mirrored ? "start" : "end"}>Femoral</text>
        <text x={mx(187)} y={200} fontFamily="monospace" fontSize="9.5" fill="#1a2530" textAnchor={mirrored ? "start" : "end"}>VSM</text>
        <text x={150} y={VIEW_H - VIEW_TY - 8} fontFamily="monospace" fontSize="13" textAnchor="middle" fill="#5c6b78">anterior</text>
      </g>

      {/* POSTERIOR: JSP + Veia Poplítea + Safena Parva + panturrilha */}
      <g transform={`translate(${colPosterior.tx},${VIEW_TY})`}>
        <g transform={mirrorTransform}>
          <path d={POSTERIOR_SILHOUETTE} fill="#f3d9bb" stroke="#a97a4e" strokeWidth={1.5} />
          <path d={POPLITEA_RIBBON} fill={corStatusProfundo(p["Veia Poplítea"])} style={clicavel} onClick={() => selecionar("profunda", "Veia Poplítea")} opacity={chaveAtiva === "profunda:Veia Poplítea" ? 0.7 : 1} stroke={chaveAtiva === "profunda:Veia Poplítea" ? "#0eb8d0" : "none"} strokeWidth={2} />
          {parvaResult.segments.map((seg, i) => (
            <path key={i} d={seg.d} fill={seg.tracejado ? "none" : seg.color} stroke={seg.tracejado ? seg.color : "none"} strokeDasharray={seg.tracejado ? "5 5" : undefined} strokeWidth={seg.tracejado ? 2 : undefined} pointerEvents="none" />
          ))}
          {interativo && hitPath(parvaHitD, () => selecionar("superficial", "Safena Parva"), "superficial:Safena Parva", chaveAtiva, 10, parvaHaloD)}
          <circle cx={150} cy={314} r={8} fill={jspCor} stroke="#fff" strokeWidth={1.5} style={clicavel} onClick={() => selecionar("superficial", "JSP")} />
          {CALF_DOTS.map((dot) => {
            const chave = `profunda:${dot.key}`;
            const ativo = chaveAtiva === chave;
            return (
              <g key={dot.key} style={clicavel} onClick={() => selecionar("profunda", dot.key)}>
                <circle cx={dot.x} cy={dot.y} r={7} fill={corStatusProfundo(p[dot.key])} stroke={ativo ? "#0eb8d0" : "#fff"} strokeWidth={ativo ? 2.5 : 1.5} />
                <text x={dot.x} y={dot.y + 3} fontFamily="monospace" fontSize="7.5" fill="#fff" textAnchor="middle" pointerEvents="none" transform={mirrored ? `translate(${2 * dot.x},0) scale(-1,1)` : undefined}>{dot.label}</text>
              </g>
            );
          })}
          {/* varizes marcadas por região (exames antigos / formulário): só exibe */}
          {VARIZ_SPOTS.filter((spot) => spot.view === "posterior" && vz[spot.regiao]).map((spot) => (
            <g key={spot.regiao} pointerEvents="none">
              <VarizIcon tipo={vz[spot.regiao]} cx={spot.x} cy={spot.y} ativo={false} escala={VARIZ_ESCALA} />
            </g>
          ))}
          {desenharTracos(VISTA_DA_COLUNA.posterior)}
          {camadaDesenho(VISTA_DA_COLUNA.posterior)}
        </g>
        <text x={mx(230)} y={310} fontFamily="monospace" fontSize="9.5" fill="#1a2530" textAnchor={mirrored ? "start" : "end"}>JSP</text>
        <text x={mx(135)} y={345} fontFamily="monospace" fontSize="9.5" fill="#1a2530" textAnchor={mirrored ? "start" : "end"}>V. Poplítea</text>
        <text x={mx(115)} y={430} fontFamily="monospace" fontSize="9.5" fill="#1a2530" textAnchor={mirrored ? "start" : "end"}>VSP</text>
        <text x={150} y={VIEW_H - VIEW_TY - 8} fontFamily="monospace" fontSize="13" textAnchor="middle" fill="#5c6b78">posterior</text>
      </g>
    </svg>
  );
}

const CANETAS_VARIZ = [
  ["Varizes Superficiais", "Superficial"],
  ["Varizes Reticulares", "Reticular"],
  ["Microvarizes", "Micro"],
  ["Tromboflebite Superficial", "Tromboflebite"],
];

// Barra do modo desenho: canetas por tipo, borracha, desfazer e limpar.
function BarraDesenhoVarizes({ ferramenta, onFerramenta, totalTracos, onDesfazer, onLimpar }) {
  const base = { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 11px", borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: "pointer", border: "1.5px solid #cfd9e2", background: "#fff", color: "#1a2530" };
  if (!ferramenta) {
    return (
      <button type="button" onClick={() => onFerramenta("Varizes Superficiais")} style={{ ...base, borderColor: "#0eb8d0", color: "#0a7f91" }}>
        <FiEdit2 /> Desenhar varizes
      </button>
    );
  }
  const ativo = (f) => ferramenta === f;
  const desabilitado = totalTracos === 0;
  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
        {CANETAS_VARIZ.map(([tipo, rotulo]) => (
          <button
            key={tipo} type="button" onClick={() => onFerramenta(tipo)} aria-pressed={ativo(tipo)}
            style={{ ...base, borderColor: ativo(tipo) ? VARIZ_CORES[tipo] : "#cfd9e2", background: ativo(tipo) ? `${VARIZ_CORES[tipo]}1a` : "#fff", color: VARIZ_CORES[tipo] }}
          >
            <VarizLinhaLegenda tipo={tipo} />
            {rotulo}
          </button>
        ))}
        <button type="button" onClick={() => onFerramenta("borracha")} aria-pressed={ativo("borracha")} style={{ ...base, borderColor: ativo("borracha") ? "#1a2530" : "#cfd9e2", background: ativo("borracha") ? "#eef2f5" : "#fff" }}>
          <FaEraser /> Borracha
        </button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
        <button type="button" onClick={onDesfazer} disabled={desabilitado} style={{ ...base, opacity: desabilitado ? 0.45 : 1, cursor: desabilitado ? "default" : "pointer" }}>
          <FiRotateCcw /> Desfazer
        </button>
        <button type="button" onClick={onLimpar} disabled={desabilitado} style={{ ...base, color: "#c0392b", opacity: desabilitado ? 0.45 : 1, cursor: desabilitado ? "default" : "pointer" }}>
          <FiTrash2 /> Limpar
        </button>
        <button type="button" onClick={() => onFerramenta(null)} style={{ ...base, background: "#28a745", borderColor: "#28a745", color: "#fff" }}>
          <FiCheck /> Concluir
        </button>
      </div>
      <div style={{ fontSize: 11.5, color: "#5c6b78", textAlign: "center" }}>
        {ferramenta === "borracha" ? "Toque num traço para apagar." : "Desenhe sobre a perna com o dedo, a caneta ou o mouse."}
      </div>
    </div>
  );
}

export default function MapaInterativo({
  aberto, onFechar,
  lado, ladoAtivo, onTrocarLado,
  nome, data,
  profundas, superficiais, magna, parva, perfurantes, observacoes, varizes,
  jsfDiametro, jspDiametro,
  onProfundas, onSuperficiais, onMagna, onParva, onPerfurantes,
  onJsfDiametro, onJspDiametro, onVarizes, onObservacao,
  onSalvarExame, onSalvarTXT, onSalvarPDF, onEnviar, onAbrirMapeamentoVisual,
  incluirMapeamentoVisualPdf, onIncluirMapeamentoVisualPdf,
  anexos = [], onFileUpload, onDrop, onDragOver, onRemoveAnexo, formatFileSize,
  embutido,
}) {
  const [selecionado, setSelecionado] = useState(null);
  const [ferramenta, setFerramenta] = useState(null);

  if (!aberto) return null;

  const l = ladoAtivo;
  const p = profundas?.[l] || {};
  const s = superficiais?.[l] || {};
  const m = magna?.[l] || {};
  const pv = parva?.[l] || {};
  const perfs = Array.isArray(perfurantes?.[l]) ? perfurantes[l] : [];
  const vz = normalizarVarizesLado(varizes?.[l]);

  function selecionar(tipo, key) {
    setSelecionado({ tipo, key });
  }
  const chaveAtiva = selecionado ? `${selecionado.tipo}:${selecionado.key}` : null;


  const selectStyle = { padding: "8px 10px", borderRadius: 6, fontSize: 14, border: "1.5px solid #0eb8d0", background: "#f7fbff", color: "#222", maxWidth: "100%" };
  const linhaCampo = { display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 };
  const rotulo = { fontSize: 12, fontWeight: 700, color: "#0a7f91" };

  let tituloPainel = "";
  let painel = null;
  if (selecionado?.tipo === "profunda") {
    tituloPainel = PROFUNDA_LABELS[selecionado.key];
    painel = (
      <div style={linhaCampo}>
        <span style={rotulo}>Achado:</span>
        <select value={p[selecionado.key]} onChange={(e) => onProfundas(l, { ...p, [selecionado.key]: e.target.value })} style={selectStyle}>
          {profOptions.map((opt) => <option key={opt}>{opt}</option>)}
        </select>
      </div>
    );
  } else if (selecionado?.tipo === "superficial" && (selecionado.key === "JSF" || selecionado.key === "JSP")) {
    tituloPainel = selecionado.key === "JSF" ? "Junção safeno-femoral (JSF)" : "Junção safeno-poplítea (JSP)";
    painel = (
      <>
        <div style={linhaCampo}>
          <span style={rotulo}>Achado:</span>
          <select value={s[selecionado.key]} onChange={(e) => onSuperficiais(l, { ...s, [selecionado.key]: e.target.value })} style={selectStyle}>
            {supOptions.map((opt) => <option key={opt}>{opt}</option>)}
          </select>
        </div>
        <div style={linhaCampo}>
          <span style={rotulo}>Diâmetro (mm):</span>
          <input
            type="number" min={0} step={0.1} placeholder="mm"
            value={(selecionado.key === "JSF" ? jsfDiametro?.[l] : jspDiametro?.[l]) || ""}
            onChange={(e) => (selecionado.key === "JSF" ? onJsfDiametro(l, e.target.value) : onJspDiametro(l, e.target.value))}
            style={{ ...selectStyle, width: 140 }}
          />
        </div>
      </>
    );
  } else if (selecionado?.tipo === "superficial" && selecionado.key === "Safena Magna") {
    tituloPainel = "Safena Magna";
    painel = (
      <>
        <div style={linhaCampo}>
          <span style={rotulo}>Achado:</span>
          <select value={s["Safena Magna"]} onChange={(e) => onSuperficiais(l, { ...s, "Safena Magna": e.target.value })} style={selectStyle}>
            {supOptions.map((opt) => <option key={opt}>{opt}</option>)}
          </select>
        </div>
        <SafenaMagnaExtra status={s["Safena Magna"]} valores={m} onChange={(val) => onMagna(l, val)} />
      </>
    );
  } else if (selecionado?.tipo === "superficial" && selecionado.key === "Safena Parva") {
    tituloPainel = "Safena Parva";
    painel = (
      <>
        <div style={linhaCampo}>
          <span style={rotulo}>Achado:</span>
          <select value={s["Safena Parva"]} onChange={(e) => onSuperficiais(l, { ...s, "Safena Parva": e.target.value })} style={selectStyle}>
            {supOptions.map((opt) => <option key={opt}>{opt}</option>)}
          </select>
        </div>
        <SafenaParvaExtra status={s["Safena Parva"]} valores={pv} onChange={(val) => onParva(l, val)} />
      </>
    );
  }

  // ● (círculo geométrico comum) em vez de emoji de bolinha colorida: emoji de
  // cor ignora o "color" do CSS e depende da fonte de emoji do sistema.
  const legenda = (
    <>
      <div style={{ display: "flex", gap: 8, fontSize: 11, color: "#5c6b78", flexWrap: "wrap", justifyContent: "center" }}>
        <span style={{ color: CORES["pérvia e competente"] }}>● suficiente</span>
        <span style={{ color: CORES["pérvia e incompetente"] }}>● insuficiente</span>
        <span style={{ color: CORES["não compressível e sem fluxo (trombose)"] }}>● trombose</span>
        <span style={{ color: CORES["recanalização parcial"] }}>● recanalização parcial</span>
        <span style={{ color: CORES["ausente"] }}>● ausente</span>
        <span style={{ color: CORES["pérvia e incompetente"] }}>▲ perfurante insuficiente</span>
        <span>Gc=Gastrocnêmicas · Ta=Tibiais Ant. · So=Soleares · Tp=Tibiais Post.</span>
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 4, fontSize: 11, color: "#5c6b78", flexWrap: "wrap", justifyContent: "center", alignItems: "center" }}>
        <span>Varizes (use Desenhar varizes):</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: VARIZ_COR }}><VarizLinhaLegenda tipo="Varizes Superficiais" /> superficiais</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: VARIZ_COR }}><VarizLinhaLegenda tipo="Varizes Reticulares" /> reticulares</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: VARIZ_COR }}><VarizLinhaLegenda tipo="Microvarizes" /> microvarizes</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: VARIZ_CORES["Tromboflebite Superficial"] }}><VarizLinhaLegenda tipo="Tromboflebite Superficial" /> tromboflebite superficial</span>
      </div>
    </>
  );

  const conteudo = (
    <>
      {/* Veias perfurantes: lista compacta (mesmos campos do formulário) */}
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Veias Perfurantes ({l}):</div>
        {perfs.map((perf, idx) => (
          <div key={idx} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
            <select
              value={perf.status}
              onChange={(e) => {
                const novoStatus = e.target.value;
                const novo = perfs.map((pf, i) => i === idx ? (
                  novoStatus === "pérvia e incompetente" ? { ...pf, status: novoStatus } : { ...pf, status: novoStatus, segmento: "", valor: "" }
                ) : pf);
                onPerfurantes(l, novo);
              }}
              style={{ ...selectStyle, fontSize: 13, padding: 6 }}
            >
              {perfurantesStatusOptions.map((opt) => <option key={opt}>{opt}</option>)}
            </select>
            {perf.status === "pérvia e incompetente" && (
              <>
                <select
                  value={perf.segmento}
                  onChange={(e) => onPerfurantes(l, perfs.map((pf, i) => i === idx ? { ...pf, segmento: e.target.value, valor: "" } : pf))}
                  style={{ ...selectStyle, fontSize: 13, padding: 6 }}
                >
                  <option value="">Selecione o segmento</option>
                  {perfurantesSegmentoOptions.map((opt) => <option key={opt}>{opt}</option>)}
                </select>
                {perf.segmento && (
                  <input
                    type="number" min={0} step={0.1} placeholder="cm" value={perf.valor}
                    onChange={(e) => onPerfurantes(l, perfs.map((pf, i) => i === idx ? { ...pf, valor: e.target.value } : pf))}
                    style={{ ...selectStyle, fontSize: 13, padding: 6, width: 70 }}
                  />
                )}
              </>
            )}
            {perfs.length > 1 && (
              <button type="button" onClick={() => onPerfurantes(l, perfs.filter((_, i) => i !== idx))} style={{ padding: "5px 10px", borderRadius: 4, border: "none", background: "#c0392b", color: "#fff", fontSize: 12, cursor: "pointer" }}>Remover</button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => onPerfurantes(l, [...perfs, { status: "pérvia e competente", segmento: "", valor: "" }])}
          style={{ padding: "6px 12px", borderRadius: 4, border: "1.5px solid #0eb8d0", background: "transparent", color: "#0a7f91", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
        >+ Adicionar perfurante</button>
      </div>

      {/* Observações do membro ativo — mesmo campo do formulário escrito. */}
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Observações ({l}):</div>
        <textarea
          value={observacoes?.[l] || ""}
          onChange={(e) => onObservacao(l, e.target.value)}
          placeholder={`Digite observações adicionais do exame do membro ${l.toLowerCase()}...`}
          style={{ width: "100%", minHeight: 50, fontSize: 13, borderRadius: 6, border: "1.5px solid #0eb8d0", padding: 8, resize: "vertical", boxSizing: "border-box" }}
        />
      </div>

      {/* Anexos: mesmas imagens (PNG/JPG) que o formulário principal anexa ao PDF */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div style={{ fontWeight: 700, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 5 }}><FiPaperclip /> Anexos:</div>
          <span style={{ fontSize: 11, color: "#5c6b78" }}>{anexos.length} arquivo(s)</span>
        </div>
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onClick={() => document.getElementById('fileInputMapa').click()}
          style={{ border: "1px dashed #0eb8d0", borderRadius: 6, padding: 10, textAlign: "center", background: "rgba(14,184,208,0.05)", cursor: "pointer", fontSize: 12, color: "#0a7f91" }}
        >
          <input id="fileInputMapa" type="file" accept=".png,.jpg,.jpeg" multiple onChange={onFileUpload} style={{ display: "none" }} />
          Clique ou arraste para anexar (PNG/JPG até 15MB)
        </div>
        {anexos.length > 0 && (
          <div style={{ maxHeight: 120, overflowY: "auto", border: "1px solid #dfe6ec", borderRadius: 6, background: "#f7f9fa", padding: 6, marginTop: 6 }}>
            {anexos.map((anexo) => (
              <div key={anexo.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: 4, background: "#fff", borderRadius: 4, marginBottom: 4, border: "1px solid #dfe6ec" }}>
                <img src={anexo.thumbnail} alt={anexo.name} style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 3, border: "1px solid #dfe6ec" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{anexo.name}</div>
                  <div style={{ fontSize: 10, color: "#5c6b78" }}>{formatFileSize(anexo.size)}</div>
                </div>
                <button onClick={() => onRemoveAnexo(anexo.id)} style={{ background: "#c0392b", color: "#fff", border: "none", borderRadius: 4, padding: "3px 8px", fontSize: 11, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><FiX /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Laudo ao vivo */}
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Laudo ({l}) — atualizado em tempo real:</div>
        <pre style={{ background: "#f7f9fa", border: "1px solid #dfe6ec", borderRadius: 8, padding: 10, fontSize: 11.5, whiteSpace: "pre-wrap", maxHeight: 320, overflowY: "auto", margin: 0 }}>
          {montarLaudo({ nome, data, lado: l, profundas, superficiais, magna, parva, jsfDiametro, jspDiametro, observacoes, perfurantes, varizes })}
        </pre>
      </div>
    </>
  );

  return (
    <MapaLayout
      embutido={embutido}
      titulo="MMII Venoso"
      onFechar={onFechar}
      lado={lado}
      ladoAtivo={ladoAtivo}
      onTrocarLado={(op) => { onTrocarLado(op); setSelecionado(null); }}
      desenho={
        <div style={{ width: "100%", maxWidth: 560, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
          <BarraDesenhoVarizes
            ferramenta={ferramenta}
            onFerramenta={(f) => { setFerramenta(f); if (f) setSelecionado(null); }}
            totalTracos={(vz.tracos || []).length}
            onDesfazer={() => onVarizes(l, { ...vz, tracos: (vz.tracos || []).slice(0, -1) })}
            onLimpar={() => {
              if (window.confirm(`Apagar todos os traços desenhados no membro ${l.toLowerCase()}?`)) onVarizes(l, { ...vz, tracos: [] });
            }}
          />
          <DesenhoMMIIVenoso
            lado={l} profundas={p} superficiais={s} magna={m} parva={pv} perfurantes={perfs} varizes={vz}
            onSelecionar={selecionar} chaveAtiva={chaveAtiva}
            ferramenta={ferramenta}
            onTracosChange={(novos) => onVarizes(l, { ...vz, tracos: novos })}
            style={{ width: "100%", maxWidth: 560, maxHeight: "74vh", display: "block" }}
          />
        </div>
      }
      legenda={legenda}
      painel={painel}
      tituloPainel={`${tituloPainel} (${l})`}
      onFecharPainel={() => setSelecionado(null)}
      conteudo={conteudo}
      incluirPdf={incluirMapeamentoVisualPdf}
      onIncluirPdf={onIncluirMapeamentoVisualPdf}
      labelIncluirPdf="Incluir Mapeamento Visual no PDF"
      onVisualizarImagem={onAbrirMapeamentoVisual}
      onSalvarTXT={onSalvarTXT}
      onSalvarPDF={onSalvarPDF} onEnviar={onEnviar}
      onSalvarExame={() => {
        if (!nome || !data) { alert("Preencha nome e data no formulário antes de salvar o exame!"); return; }
        onSalvarExame();
      }}
    />
  );
}
